import { useRef } from 'react';
import { Checkbox, FormControl, FormControlLabel, FormGroup, InputLabel, MenuItem, Select } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { CloudUpload, Delete } from '@/components/Icon';
import { InputFileLabel, InputFileWrapper } from '@/components/UI/Input/InputFile/styles';
import { Text } from '@/components/UI/Typography/Text';
import { theme } from '@/theme/Theme';

export const ToggleGroup = <T extends string>({
  options,
  values,
  onChange,
  name,
  disabled = false,
}: {
  options: Array<{ value: T; label: string }>;
  values: T[];
  onChange: (nextValue: T[]) => void;
  name?: string;
  disabled?: boolean;
}) => {
  const toggle = (value: T) => {
    if (disabled) return;

    if (values.includes(value)) {
      onChange(values.filter((item) => item !== value));
      return;
    }

    onChange([...values, value]);
  };

  return (
    <FormGroup data-volunteer-field={name}>
      {options.map((option) => (
        <FormControlLabel
          key={option.value}
          control={
            <Checkbox
              name={name}
              value={option.value}
              checked={values.includes(option.value)}
              disabled={disabled}
              onChange={() => toggle(option.value)}
            />
          }
          label={option.label}
        />
      ))}
    </FormGroup>
  );
};

export const BooleanSelect = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: boolean;
  onChange: (nextValue: boolean) => void;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();

  return (
    <FormControl fullWidth variant="standard">
      <InputLabel>{label}</InputLabel>
      <Select value={value ? 'yes' : 'no'} label={label} onChange={(event) => onChange(event.target.value === 'yes')}>
        <MenuItem value="no">{t('pbl.common.no', { defaultValue: 'No' })}</MenuItem>
        <MenuItem value="yes">{t('pbl.common.yes', { defaultValue: 'Yes' })}</MenuItem>
      </Select>
    </FormControl>
  );
};

export const UploadFileField = ({
  disabled = false,
  accept = '.pdf',
  clearAriaLabel,
  fileName,
  isUploading,
  onClear,
  onSelectFile,
  placeholder,
  name,
}: {
  accept?: string;
  disabled?: boolean;
  clearAriaLabel: string;
  fileName?: string;
  isUploading: boolean;
  onClear: () => void;
  onSelectFile: (file: File) => void;
  placeholder: string;
  name?: string;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const hasFile = Boolean(fileName);

  const clearFile = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }

    onClear();
  };

  return (
    <InputFileWrapper data-volunteer-field={name}>
      <InputFileLabel>
        <Text
          fontWeight={400}
          fontSize={theme.fontSizes.sm}
          color={theme.colors.gray500}
          style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {isUploading ? t('pbl.common.upload.uploading', { defaultValue: 'Uploading...' }) : fileName || placeholder}
        </Text>
        <input
          ref={inputRef}
          name={name}
          hidden
          accept={accept}
          type="file"
          disabled={disabled || isUploading}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) {
              onSelectFile(file);
            }
          }}
        />
        <CloudUpload.CloudUploadSVG style={{ marginLeft: 'auto' }} />
      </InputFileLabel>
      {!disabled && !isUploading && hasFile && (
        <Delete.FormDelete type="button" aria-label={clearAriaLabel} title={clearAriaLabel} onClick={clearFile}>
          <Delete.DeleteSVG />
        </Delete.FormDelete>
      )}
    </InputFileWrapper>
  );
};
